// The UGC report that goes with a demo wall (D-068): what people post about the brand on
// Instagram, how much it is liked, and what that could mean on the brand's website. Written for
// a brand or online manager; private, unlisted and printable (Save as PDF).
import type { DemoStats, DemoReport as Report } from "@brandwall/contracts";
import type React from "react";
import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { ApiError, api, compact } from "../api";

interface ReportData {
  /** Screenshots of the wall on the brand's real homepage (D-076). */
  shots: {
    desktop: { src: string; width: number; height: number } | null;
    phone: { src: string; width: number; height: number } | null;
    /** On a product page (D-088). */
    product: {
      desktop: { src: string; width: number; height: number } | null;
      phone: { src: string; width: number; height: number } | null;
    } | null;
  } | null;
  brand: {
    name: string;
    website: string | null;
    logoUrl: string | null;
    colors: string[];
    instagram: string | null;
  };
  hashtags: string[];
  report: Report | null;
  /** The pitch video (D-106). */
  video: string | null;
  /** Creators chosen for the brand (D-104): not on the wall. */
  prospects: {
    handle: string;
    link: string;
    followers: number | null;
    engagement: number | null;
    niches: string[];
    reason: string;
    source: string;
  }[];
  /** How the brand compares with its competitors (D-105). */
  compare: {
    yours: { ugcCount: number; periodDays: number | null };
    competitors: {
      handle: string;
      name: string | null;
      ugcCount: number;
      periodDays: number;
      topPost: { link: string; username?: string; likes?: number } | null;
    }[];
  } | null;
  expiresAt: string;
  wallLink: string;
  topPosts: {
    id: string;
    own: boolean;
    creator: string;
    caption: string | null;
    permalink: string | null;
    postedAt: string | null;
    likes: number | null;
    comments: number | null;
    hashtag: string | null;
    image: string;
  }[];
}

const n = (v: number | null | undefined) => (v == null ? "–" : compact(Math.round(v)));
const date = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "–";
const tags = (list: string[]) =>
  list
    .map((t) => `#${t}`)
    .join(", ")
    .replace(/, ([^,]*)$/, " and $1");

function Kpi({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <div className="rp-kpi">
      <span>{label}</span>
      <b>{value}</b>
      {note && <small>{note}</small>}
    </div>
  );
}

/** A few plain sentences from the numbers: what was found and what it could mean. */
function summary(d: ReportData, r: Report) {
  const c = r.community;
  const out: string[] = [];
  const looked = r.communityLooked ?? c.posts;
  const rated = Boolean(r.ai);
  if (c.posts > 0) {
    out.push(
      rated && looked > c.posts
        ? `We looked at the ${n(looked)} most recent public posts with ${tags(d.hashtags)} we could find. ${n(c.posts)} of them are about ${d.brand.name}, from ${n(c.creators)} different accounts. The rest use the hashtag for something else.`
        : `People posted at least ${n(c.posts)} photos and videos with ${tags(d.hashtags)} from ${n(c.creators)} different accounts.`,
    );
    out.push(
      `Together they got ${n(c.likes)} likes and ${n(c.comments)} comments. Every one of them is a real person showing ${d.brand.name} in their own life.`,
    );
  } else {
    out.push(
      `We found few public posts with ${tags(d.hashtags) || "the brand's hashtags"} right now. A wall can start with ${d.brand.name}'s own posts and grow as customers are invited to tag.`,
    );
  }
  const own = r.account?.stats;
  if (own?.avgLikes != null && c.avgLikes != null && c.posts > 0) {
    out.push(
      c.avgLikes >= own.avgLikes
        ? `A customer post gets ${n(c.avgLikes)} likes on average: as many as ${d.brand.name}'s own posts (${n(own.avgLikes)}) or more.`
        : `A customer post gets ${n(c.avgLikes)} likes on average, next to ${n(own.avgLikes)} on ${d.brand.name}'s own posts. Customers reach their own followers, on top of yours.`,
    );
  }
  return out;
}

function HashtagTable({
  rows,
  brand,
  rated,
}: {
  rows: (DemoStats & { tag: string; looked?: number })[];
  brand: string;
  rated: boolean;
}) {
  // Columns nobody has a number for are left out, not shown as dashes.
  const liked = rows.some((h) => h.avgLikes != null);
  return (
    <div className="rp-table">
      <table>
        <thead>
          <tr>
            <th>Hashtag</th>
            {rated && <th>Looked at</th>}
            <th>{rated ? `About ${brand}` : "Posts found"}</th>
            <th>Accounts</th>
            {liked && <th>Likes</th>}
            {liked && <th>Comments</th>}
            {liked && <th>Likes a post</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((h) => (
            <tr key={h.tag}>
              <td>
                <b>#{h.tag}</b>
              </td>
              {rated && <td>{n(h.looked ?? h.posts)}</td>}
              <td>{n(h.posts)}</td>
              <td>{n(h.creators)}</td>
              {liked && <td>{n(h.likes)}</td>}
              {liked && <td>{n(h.comments)}</td>}
              {liked && <td>{n(h.avgLikes)}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Horizontal bars, longest first. */
function Bars({
  rows,
}: {
  rows: { label: string; value: number; note?: string; soft?: boolean }[];
}) {
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <div className="rp-hbars">
      {rows.map((r, i) => (
        <div key={r.label} className="rp-hbar">
          <span title={r.label}>{r.label}</span>
          <span className="rp-track">
            <span
              className={`rp-fill${r.soft ? " soft" : ""}`}
              style={{ width: `${(r.value / max) * 100}%`, animationDelay: `${i * 80}ms` }}
            />
            <b>{r.note ?? n(r.value)}</b>
          </span>
        </div>
      ))}
    </div>
  );
}

/** Columns, one or two stacked parts each. */
function Columns({ rows }: { rows: { label: string; main: number; extra?: number }[] }) {
  const max = Math.max(1, ...rows.map((r) => r.main + (r.extra ?? 0)));
  return (
    <>
      <div className="rp-cols">
        {rows.map((r, i) => (
          <div key={r.label} className="rp-col">
            {r.main + (r.extra ?? 0) > 0 && <em>{n(r.main + (r.extra ?? 0))}</em>}
            {r.extra ? (
              <i
                className="soft"
                style={{ height: `${(r.extra / max) * 100}%`, animationDelay: `${i * 50}ms` }}
              />
            ) : null}
            <i style={{ height: `${(r.main / max) * 100}%`, animationDelay: `${i * 50}ms` }} />
          </div>
        ))}
      </div>
      <div className="rp-axis">
        {rows.map((r) => (
          <span key={r.label}>{r.label}</span>
        ))}
      </div>
    </>
  );
}

const monthName = (m: string) =>
  new Date(`${m}-01T00:00:00Z`).toLocaleDateString("en-GB", { month: "short", timeZone: "UTC" });

function Charts({ d, r }: { d: ReportData; r: Report }) {
  const c = r.community;
  const own = r.account?.stats;
  const charts: React.ReactNode[] = [];
  const tagged = r.hashtags.filter((h) => h.posts > 0);
  if (tagged.length > 1 || (tagged.length === 1 && own?.posts))
    charts.push(
      <div key="tags" className="rp-chart">
        <h3>Where the posts are</h3>
        <small>
          {r.ai
            ? `Posts about ${d.brand.name} for each hashtag`
            : "Public posts found for each hashtag"}
        </small>
        <Bars
          rows={[
            ...[...tagged]
              .sort((a, b) => b.posts - a.posts)
              .map((h) => ({ label: `#${h.tag}`, value: h.posts })),
            ...(own?.posts
              ? [{ label: `@${r.account?.username}`, value: own.posts, soft: true }]
              : []),
          ]}
        />
      </div>,
    );
  if (c.avgLikes != null && own?.avgLikes != null)
    charts.push(
      <div key="likes" className="rp-chart">
        <h3>Likes on a post</h3>
        <small>On average, customers' posts next to {d.brand.name}'s own</small>
        <Bars
          rows={[
            { label: "Customers", value: c.avgLikes },
            { label: `@${r.account?.username}`, value: own.avgLikes, soft: true },
          ]}
        />
        {c.avgComments != null && own.avgComments != null && (
          <>
            <h3>Comments on a post</h3>
            <Bars
              rows={[
                { label: "Customers", value: c.avgComments },
                { label: `@${r.account?.username}`, value: own.avgComments, soft: true },
              ]}
            />
          </>
        )}
      </div>,
    );
  const buckets = r.likeBuckets ?? [];
  if (buckets.some((b) => b.posts > 0) && c.posts >= 5)
    charts.push(
      <div key="spread" className="rp-chart">
        <h3>How liked customer posts are</h3>
        <small>Customer posts by the number of likes each got</small>
        <Columns rows={buckets.map((b) => ({ label: b.label, main: b.posts }))} />
      </div>,
    );
  const months = r.months ?? [];
  if (months.filter((m) => m.community + m.own > 0).length >= 3)
    charts.push(
      <div key="months" className="rp-chart wide">
        <h3>Posts each month</h3>
        <Columns
          rows={months.map((m) => ({ label: monthName(m.month), main: m.community, extra: m.own }))}
        />
        <div className="rp-legend">
          <span>Customers</span>
          <span className="soft">{d.brand.name}</span>
        </div>
      </div>,
    );
  if (!charts.length) return null;
  return (
    <section className="rp-section">
      <h2>At a glance</h2>
      <div className="rp-charts">{charts}</div>
    </section>
  );
}

/** "How you compare" (D-105): customer posts for the brand and each competitor. */
function Compare({ d }: { d: ReportData }) {
  const c = d.compare;
  if (!c) return null;
  const days = new Set([c.yours.periodDays, ...c.competitors.map((x) => x.periodDays)]);
  const one = days.size === 1 && c.yours.periodDays ? c.yours.periodDays : null;
  const rows = [
    { label: d.brand.name, value: c.yours.ugcCount, days: c.yours.periodDays, you: true },
    ...c.competitors.map((x) => ({
      label: x.name ?? `@${x.handle}`,
      value: x.ugcCount,
      days: x.periodDays,
      you: false,
    })),
  ].sort((a, b) => b.value - a.value);
  const rank = rows.findIndex((r) => r.you) + 1;
  return (
    <section className="rp-section">
      <h2>How you compare</h2>
      <p>
        {rank === 1
          ? `Customers post more about ${d.brand.name} than about the ${c.competitors.length === 1 ? "competitor" : `${c.competitors.length} competitors`} we looked at.`
          : `${d.brand.name} comes ${rank === 2 ? "second" : rank === 3 ? "third" : `number ${rank}`} of ${rows.length} in customer posts. The posts are there; a wall puts them to work.`}
      </p>
      <div className="rp-chart">
        <h3>Customer posts{one ? `, last ${one} days` : ""}</h3>
        <Bars
          rows={rows.map((r) => ({
            label: r.label,
            value: r.value,
            soft: !r.you,
            note: one || !r.days ? undefined : `${n(r.value)} in ${r.days} days`,
          }))}
        />
      </div>
      {c.competitors.some((x) => x.topPost) && (
        <ul className="rp-list small">
          {c.competitors
            .filter((x) => x.topPost)
            .map((x) => (
              <li key={x.handle}>
                {x.name ?? `@${x.handle}`}'s most liked customer post:{" "}
                <a href={x.topPost!.link} target="_blank" rel="noreferrer">
                  {x.topPost!.username ? `@${x.topPost!.username}` : "on Instagram"}
                </a>
                {x.topPost!.likes != null && ` (${n(x.topPost!.likes)} likes)`}
              </li>
            ))}
        </ul>
      )}
    </section>
  );
}

/** Creators chosen for the brand (D-104), apart from the wall. */
function Prospects({ d }: { d: ReportData }) {
  if (!d.prospects.length) return null;
  return (
    <section className="rp-section rp-break">
      <h2>Creators who already post about you</h2>
      <p>
        {d.prospects.length === 1 ? "One creator" : `${d.prospects.length} creators`} we picked for{" "}
        {d.brand.name}: they show {d.brand.name} or brands like it, and fit your style. They aren't
        on the demo wall; they're people you could work with.
      </p>
      <div className="rp-table">
        <table>
          <thead>
            <tr>
              <th>Creator</th>
              <th>Followers</th>
              <th>Engagement</th>
              <th>Niches</th>
              <th>Why</th>
            </tr>
          </thead>
          <tbody>
            {d.prospects.map((p) => (
              <tr key={p.handle}>
                <td>
                  <a href={p.link} target="_blank" rel="noreferrer">
                    <b>@{p.handle}</b>
                  </a>
                </td>
                <td>{n(p.followers)}</td>
                <td>{p.engagement == null ? "–" : `${p.engagement}%`}</td>
                <td>{p.niches.join(", ") || "–"}</td>
                <td className="rp-wrap">{p.reason}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function DemoReport() {
  const { token } = useParams();
  const [d, setD] = useState<ReportData | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.append(meta);
    api<ReportData>(`/v1/public/demos/${token}/report`)
      .then((r) => {
        setD(r);
        document.title = `UGC report for ${r.brand.name} · Brandwall`;
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : "This report isn't available."));
    return () => meta.remove();
  }, [token]);
  if (error) return <div className="onb">{error}</div>;
  if (!d) return <div className="onb muted">…</div>;
  const r = d.report;
  const accent = d.brand.colors[0] ?? "#10302a";
  if (!r)
    return (
      <div className="onb muted">
        Brandwall is still collecting posts for this report. Check back in a minute.
      </div>
    );
  const c = r.community;
  const acc = r.account;
  return (
    <div className="rp" style={{ ["--accent" as string]: accent }}>
      <div className="demo-flag rp-noprint">
        Report by Brandwall · not affiliated with {d.brand.name} · private link
      </div>
      <div className="rp-page">
        <header className="rp-head">
          <div>
            <span className="rp-eyebrow">Customer content report</span>
            <h1>{d.brand.name} on Instagram</h1>
            <p className="muted">
              What people post about {d.brand.name}, and what it could do on{" "}
              {d.brand.website?.replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, "") ??
                "your website"}
              . Measured on {date(r.collectedAt)}.
            </p>
          </div>
          {d.brand.logoUrl && <img src={d.brand.logoUrl} alt={d.brand.name} className="rp-logo" />}
        </header>

        <section className="rp-kpis">
          <Kpi
            label={r.ai ? `Customer posts about ${d.brand.name}` : "Customer posts found"}
            value={n(c.posts)}
            note={
              r.ai && r.communityLooked
                ? `of ${n(r.communityLooked)} recent posts with ${tags(d.hashtags)}`
                : tags(d.hashtags)
            }
          />
          <Kpi label="Different accounts" value={n(c.creators)} />
          <Kpi label="Likes on them" value={n(c.likes)} />
          <Kpi label="Comments on them" value={n(c.comments)} />
        </section>

        <section className="rp-section">
          <h2>In short</h2>
          {summary(d, r).map((s) => (
            <p key={s}>{s}</p>
          ))}
        </section>

        <Charts d={d} r={r} />

        {r.ai && r.ai.looked > 0 && (
          <section className="rp-ai">
            <b className="big">{r.ai.picked}</b>
            <span>
              of the {r.ai.looked} pictures we looked at clearly show{" "}
              {r.ai.sells ? <b>{r.ai.sells.toLowerCase()}</b> : `${d.brand.name}'s products`} in
              real use, good enough for {d.brand.name}'s website. The demo wall shows the best of
              them. Brandwall's AI does this for every new post.
            </span>
          </section>
        )}

        {acc && (
          <section className="rp-section">
            <h2>@{acc.username}</h2>
            <div className="rp-kpis small">
              <Kpi label="Followers" value={n(acc.followers)} />
              <Kpi label="Recent posts measured" value={n(acc.stats.posts)} />
              <Kpi label="Likes a post" value={n(acc.stats.avgLikes)} />
              <Kpi label="Comments a post" value={n(acc.stats.avgComments)} />
              <Kpi
                label="Engagement"
                value={acc.engagementRate != null ? `${acc.engagementRate}%` : "–"}
                note="likes and comments a post, of followers"
              />
            </div>
          </section>
        )}

        {r.hashtags.length > 0 && (
          <section className="rp-section">
            <h2>Hashtags</h2>
            <HashtagTable rows={r.hashtags} brand={d.brand.name} rated={Boolean(r.ai)} />
            <p className="small muted">
              Instagram doesn't share how many posts a hashtag has, so these numbers come from the
              most recent public posts we could find, up to about 110 for each hashtag. The real
              number is much higher.
              {r.ai &&
                ` Likes and comments count only posts about ${d.brand.name}, picked by Brandwall's AI.`}
            </p>
          </section>
        )}

        {r.ai && d.topPosts.length > 0 && (
          <section className="rp-section rp-break">
            <h2>{r.ai ? `Most liked posts about ${d.brand.name}` : "Most liked posts"}</h2>
            <div className="rp-posts">
              {d.topPosts.map((p) => (
                <a
                  key={p.id}
                  className="rp-post"
                  href={p.permalink ?? undefined}
                  target="_blank"
                  rel="noreferrer"
                >
                  <img src={p.image} alt={p.caption?.slice(0, 80) ?? `Post by @${p.creator}`} />
                  <span>
                    <b>
                      {p.own ? `@${p.creator} (you)` : p.creator ? `@${p.creator}` : "A customer"}
                    </b>
                    <small>
                      {n(p.likes)} likes · {n(p.comments)} comments
                      {p.hashtag ? ` · #${p.hashtag}` : ""}
                    </small>
                  </span>
                </a>
              ))}
            </div>
          </section>
        )}

        <Compare d={d} />

        <Prospects d={d} />

        {d.video && (
          <section className="rp-section rp-noprint">
            <h2>A short film for {d.brand.name}</h2>
            {/* biome-ignore lint/a11y/useMediaCaption: a narrated pitch without a captions file */}
            <video className="rp-shot" src={d.video} controls playsInline preload="metadata" />
          </section>
        )}

        <section className="rp-section">
          <h2>What it could mean for your website</h2>
          <p>
            Brandwall finds new customer posts about {d.brand.name} every day, asks each creator for
            permission, and only posts with a yes go on your walls.
          </p>
          <ul className="rp-list">
            <li>
              <b>Real customers, on your pages.</b> Show how people actually use {d.brand.name} on
              the homepage and next to the products they wear or use.
            </li>
            <li>
              <b>Fresh without a photo shoot.</b> New posts arrive every week. You choose which ones
              go up.
            </li>
            <li>
              <b>Permission, recorded.</b> Creators say yes with one tap, and the proof is kept with
              each post.
            </li>
          </ul>
        </section>

        {d.shots?.desktop && (
          <section className="rp-section rp-break">
            <h2>Your homepage, with the wall</h2>
            <img
              className="rp-shot"
              src={d.shots.desktop.src}
              width={d.shots.desktop.width}
              height={d.shots.desktop.height}
              alt={`The Brandwall wall on ${d.brand.name}'s homepage`}
            />
          </section>
        )}

        {(d.shots?.product?.desktop ?? d.shots?.product?.phone) && (
          <section className="rp-section rp-break">
            <h2>A product page, with the wall</h2>
            <img
              className="rp-shot"
              src={(d.shots.product.desktop ?? d.shots.product.phone)!.src}
              width={(d.shots.product.desktop ?? d.shots.product.phone)!.width}
              height={(d.shots.product.desktop ?? d.shots.product.phone)!.height}
              alt={`The Brandwall wall on one of ${d.brand.name}'s product pages`}
            />
          </section>
        )}

        <section className="rp-cta">
          <div>
            <h2>See it with your posts</h2>
            <p>We made a private demo wall from {d.brand.name}'s public posts.</p>
          </div>
          <div className="row wrap rp-noprint">
            <a className="btn" href={d.wallLink}>
              Open the demo wall
            </a>
            <button type="button" className="btn secondary" onClick={() => window.print()}>
              Save as PDF
            </button>
          </div>
          <p className="small rp-printonly">Demo wall: {d.wallLink}</p>
        </section>

        <footer className="rp-foot small muted">
          Numbers from public Instagram posts, measured {date(r.collectedAt)}. Brandwall is not
          affiliated with {d.brand.name}, and no post has been shown anywhere. Brandwall ·
          brandwall.app
        </footer>
      </div>
    </div>
  );
}
